import { Component } from '@theme/component';

/**
 * @typedef {object} VariantOptionSelectRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary trigger element
 * @property {HTMLSelectElement} select - The hidden select element
 * @property {HTMLElement} currentValue - The current value display span
 *
 * @extends {Component}
 */
export class VariantOptionSelect extends Component {
  requiredRefs = ['details', 'summary', 'select', 'currentValue', 'panel'];

  /** @type {number | null} */
  #pendingElevationTimeout = null;

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    this.#cancelPendingElevationCleanup();

    if (isOpen) {
      // Drop any sibling dropdown's elevation immediately rather than waiting
      // for its own close-cleanup timer (see #scheduleElevationCleanup): if
      // that timer hasn't fired yet, both dropdowns briefly share the same
      // raised z-index, and DOM order (not which one is actually open) decides
      // which one paints on top — a sibling further down the picker would
      // otherwise render over this one while it's opening.
      this.#clearSiblingElevation();

      // Raise the stacking context immediately so the panel clears surrounding
      // content for the entire open transition (see #scheduleElevationCleanup
      // for why this can't just be a CSS `:has(details[open])` toggle).
      this.setAttribute('data-panel-open', 'true');

      // Move focus to selected option when panel opens
      const selectedOption = this.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    } else {
      this.#scheduleElevationCleanup();
    }
  };

  /**
   * Removes the raised stacking context only after the slower of the two
   * transitions that can still be animating the panel closed would have
   * finished, instead of the instant `[open]` attribute removal. Dropping it
   * immediately (e.g. via a CSS `:has(details[open])` toggle) happens before
   * the fade/slide-out completes, so the still-visible closing panel can
   * briefly render behind page content again. The panel has its own close
   * transition, but its parent `::details-content` (from
   * accordion-custom-component) runs a separate, slower one — matching only
   * the panel's own duration let the elevation drop while the parent was
   * still visibly collapsing.
   *
   * A `transitionend` listener would be more precise, but closing can be
   * triggered from several places (own summary, Escape, selecting an option,
   * or `auto-close-details.js` closing this panel as a side effect of another
   * one opening) — a plain timer matched to the CSS durations is simpler and
   * doesn't depend on that event reliably reaching us in every case.
   */
  #scheduleElevationCleanup() {
    const { panel } = this.refs;
    const panelDurationMs = panel instanceof HTMLElement ? getTransitionDurationMs(panel) : 0;
    const accordionDurationMs = getCssTimeMs(this, '--animation-speed-slow');
    const durationMs = Math.max(panelDurationMs, accordionDurationMs);

    this.#pendingElevationTimeout = window.setTimeout(() => {
      this.removeAttribute('data-panel-open');
      this.#pendingElevationTimeout = null;
    }, durationMs);
  }

  #cancelPendingElevationCleanup() {
    if (this.#pendingElevationTimeout === null) return;
    window.clearTimeout(this.#pendingElevationTimeout);
    this.#pendingElevationTimeout = null;
  }

  /**
   * Strips the raised stacking context from other dropdowns in the same
   * variant picker, canceling their pending cleanup timers instead of letting
   * them expire on their own schedule.
   */
  #clearSiblingElevation() {
    const siblings = this.closest('variant-picker')?.querySelectorAll('variant-option-select') ?? [];

    for (const sibling of siblings) {
      if (sibling === this || !(sibling instanceof VariantOptionSelect)) continue;
      sibling.#cancelPendingElevationCleanup();
      sibling.removeAttribute('data-panel-open');
    }
  }

  /**
   * `on:click`/`on:keydown` are declared on this element (see the theme's
   * component.js event delegation), not on individual `[role="option"]` rows.
   * That means the delegated handler's `event.target` is proxied to point at
   * this element rather than the row the user actually interacted with, so we
   * read the real element from `composedPath()` instead.
   */
  #resolveEventTarget(event) {
    return event.composedPath?.()[0] ?? event.target;
  }

  /**
   * Handles option click
   */
  handleOptionClick = (event) => {
    const target = this.#resolveEventTarget(event);

    if (!(target instanceof Element)) return;

    const option = target.closest('[role="option"]');
    if (!option || !(option instanceof HTMLElement)) return;

    // Don't select disabled options
    if (option.getAttribute('aria-disabled') === 'true') return;

    this.#selectOption(option);
  };

  /**
   * Handles keyboard navigation
   */
  handleKeyDown = (event) => {
    const options = Array.from(this.querySelectorAll('[role="option"]:not([aria-disabled="true"])')).filter(
      (o) => o instanceof HTMLElement
    );
    if (!options.length) return;

    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ': {
        const target = this.#resolveEventTarget(event);

        if (target instanceof Element) {
          const targetOption = target.closest('[role="option"]:not([aria-disabled="true"])');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;
      }

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Moves focus between options
   */
  #moveFocus(options, newIndex) {
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers change event
   */
  #selectOption(option) {
    if (!(option instanceof HTMLElement)) return;

    const { select, currentValue } = this.refs;
    if (!(select instanceof HTMLSelectElement) || !(currentValue instanceof HTMLElement)) return;

    const value = option.dataset.value;
    if (!value) return;

    // Only proceed if the value actually changed
    if (select.value === value) {
      this.#closeDropdown();
      return;
    }

    // Set the select value and dispatch change event
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // Update aria-selected states
    this.querySelectorAll('[role="option"]').forEach((opt) => {
      opt.setAttribute('aria-selected', 'false');
    });
    option.setAttribute('aria-selected', 'true');

    // Update the displayed current value
    currentValue.textContent = option.textContent.trim();

    // Close the panel
    this.#closeDropdown();
  }

  /**
   * Closes the dropdown and restores focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;

    // Reset roving tabindex to the selected option
    const options = this.querySelectorAll('[role="option"]');
    const selectedOption = this.querySelector('[aria-selected="true"]');

    options.forEach((opt) => {
      if (opt instanceof HTMLElement) {
        opt.tabIndex = -1;
      }
    });

    if (selectedOption instanceof HTMLElement) {
      selectedOption.tabIndex = 0;
    }

    details.open = false;
    if (summary instanceof HTMLElement) {
      summary.focus();
    }
  }
}

/**
 * Reads the longest `transition-duration` declared on an element, in milliseconds.
 *
 * @param {HTMLElement} element
 * @returns {number}
 */
function getTransitionDurationMs(element) {
  const durations = getComputedStyle(element).transitionDuration.split(',');
  const seconds = Math.max(...durations.map((value) => parseFloat(value) || 0));
  return seconds * 1000;
}

/**
 * Reads a CSS time custom property (e.g. `--animation-speed-slow`) in milliseconds.
 *
 * @param {HTMLElement} element
 * @param {string} propertyName
 * @returns {number}
 */
function getCssTimeMs(element, propertyName) {
  const value = getComputedStyle(element).getPropertyValue(propertyName);
  return (parseFloat(value) || 0) * 1000;
}

if (!customElements.get('variant-option-select')) {
  customElements.define('variant-option-select', VariantOptionSelect);
}
